import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Brain,
  Shield,
  Zap,
  FileSearch,
  MessagesSquare,
  BookOpenCheck,
  ChevronDown,
  CheckCircle2,
  Quote,
  Play,
  TrendingUp,
  Clock,
  Target,
  Layers,
  Lock,
  Send,
  Paperclip,
  Copy,
  ThumbsUp,
  Trash2,
  Info,
  Loader2,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import "./NewLanding.css";

const NewLanding = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  // ---------- Hero: animated word rotator ----------
  const rotatingWords = useMemo(
    () => ["Research.", "Arguments.", "Strategy.", "Drafting.", "Debate."],
    [],
  );
  const [wordIndex, setWordIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setWordIndex((i) => (i + 1) % rotatingWords.length),
      2200,
    );
    return () => clearInterval(id);
  }, [rotatingWords.length]);

  // ---------- Hero: research-page simulator ----------
  const heroQuery =
    "What's the strongest defense against a breach-of-contract claim when delivery was late but partial cure was tendered?";
  const heroModels = useMemo(
    () => [
      {
        key: "chatgpt",
        name: "ChatGPT",
        accent: "#10a37f",
        logo: "/openai-icon.svg",
        body: [
          { type: "h3", text: "I. Issue(s) Presented" },
          {
            type: "ul",
            items: [
              "Whether late delivery, partially cured within the contractual cure window, constitutes a material breach.",
              "Whether the buyer's continued acceptance evidences waiver of strict timing.",
            ],
          },
          { type: "h3", text: "II. Relevant Rules / Legal Principles" },
          {
            type: "ul",
            items: [
              "Restatement (Second) of Contracts §237 — substantial performance defeats material breach where essential benefit was received.",
              "General principle: an effective cure tender extinguishes a material-breach claim.",
              "Courts treat 'time of the essence' clauses with varying degrees of strictness by jurisdiction.",
            ],
          },
          { type: "h3", text: "III. Analysis" },
          {
            type: "p",
            text: "The strongest defense centers on substantial performance. The cure window expressly preserved in §8.3 of the agreement supports the argument that timely tender of cure preserved the bargain's essential benefit.",
          },
          {
            type: "p",
            text: "Opposing counsel will likely lean on the TOE clause; however, courts in this jurisdiction generally require express, unambiguous TOE language with a corresponding forfeiture provision.",
          },
          { type: "h3", text: "IV. Application" },
          {
            type: "ul",
            items: [
              "Factual strength: contemporaneous emails show buyer's continued acceptance after the cure tender.",
              "Factual weakness: a single internal memo references buyer's frustration with the delay.",
              "Risk: MEDIUM — outcome depends on jurisdictional treatment of TOE.",
            ],
          },
          { type: "h3", text: "V. Alternative Interpretations" },
          {
            type: "ul",
            items: [
              "If the TOE clause is read strictly, even a brief delay may constitute breach.",
              "Some jurisdictions treat partial cure as insufficient absent express acceptance.",
            ],
          },
          { type: "h3", text: "VI. Conclusion" },
          {
            type: "p",
            text: "Substantial performance is the strongest defensive theory. Anchor on §237 and the documented cure tender. Material risk remains tied to the TOE clause's interpretation.",
          },
          { type: "h3", text: "VII. Additional Facts Needed" },
          {
            type: "ul",
            items: [
              "Full email chain post-cure tender.",
              "Buyer's contemporaneous internal documentation of damages.",
              "Drafting history of §8.3 (cure window) for intent evidence.",
            ],
          },
        ],
      },
      {
        key: "claude",
        name: "Claude",
        accent: "#d97757",
        logoCdn: "claude",
        body: [
          { type: "h3", text: "I. Issue(s) Presented" },
          {
            type: "ul",
            items: [
              "Whether seller's late delivery, followed by tendered cure, constitutes material breach.",
              "Whether UCC §2-508 cure rights override the TOE clause for the goods component.",
            ],
          },
          { type: "h3", text: "II. Relevant Rules / Legal Principles" },
          {
            type: "ul",
            items: [
              "UCC §2-508 — seller's right to cure on tender of conforming goods.",
              "Material breach requires substantial deprivation of expected benefit.",
              "Mitigation doctrine — non-breaching party must take reasonable steps to limit damages.",
            ],
          },
          { type: "h3", text: "III. Analysis" },
          {
            type: "p",
            text: "Reframe the dispute around the tendered cure rather than the original delay. The statutory cure right under UCC §2-508 applies if any goods component exists. Documentation of plaintiff's response to cure tender will be dispositive on the material-breach element.",
          },
          { type: "h3", text: "IV. Application" },
          {
            type: "ul",
            items: [
              "Strength: cure tender was timely and commercially reasonable on its face.",
              "Weakness: ambiguity over whether the contract is goods-or-services dominant.",
              "Risk: MEDIUM — turns on predominant-purpose analysis.",
            ],
          },
          { type: "h3", text: "V. Alternative Interpretations" },
          {
            type: "ul",
            items: [
              "Under a service-dominant reading, common-law cure rights are narrower than UCC §2-508.",
              "Plaintiff may argue refusal of cure was reasonable, defeating the defense.",
            ],
          },
          { type: "h3", text: "VI. Conclusion" },
          {
            type: "p",
            text: "Position is defensible. Lead with the cure-tender theory and reserve UCC §2-508 as fallback if goods component is established. Document mitigation contemporaneously to defeat damages on the back end.",
          },
          { type: "h3", text: "VII. Additional Facts Needed" },
          {
            type: "ul",
            items: [
              "Bill of materials to establish predominant-purpose test.",
              "Plaintiff's mitigation log and post-cure communications.",
            ],
          },
        ],
      },
      {
        key: "gemini",
        name: "Gemini",
        accent: "#4285f4",
        logoCdn: "googlegemini",
        body: [
          { type: "h3", text: "I. Issue(s) Presented" },
          {
            type: "ul",
            items: [
              "Whether late delivery constitutes material breach where partial cure was tendered.",
              "Which jurisdiction's standard governs the time-of-essence inquiry.",
            ],
          },
          { type: "h3", text: "II. Relevant Rules / Legal Principles" },
          {
            type: "ul",
            items: [
              "2nd Circuit: strict enforcement where TOE is expressly stated.",
              "9th Circuit: totality-of-circumstances; cure window typically controls absent prejudice.",
              "Estoppel: continued acceptance of performance may waive strict timing.",
            ],
          },
          { type: "h3", text: "III. Analysis" },
          {
            type: "p",
            text: "Jurisdictional treatment is the pivot point. If the dispute is venued in the 2nd Circuit, the express TOE clause likely controls and substantial performance arguments weaken materially. If in the 9th Circuit, the cure window is the controlling provision absent demonstrable prejudice.",
          },
          { type: "h3", text: "IV. Application" },
          {
            type: "ul",
            items: [
              "Strength: forum-selection clause selects favorable jurisdiction.",
              "Weakness: TOE clause is express, not implied — limits flexibility.",
              "Risk: MEDIUM-HIGH if venued in strict-TOE jurisdiction.",
            ],
          },
          { type: "h3", text: "V. Alternative Interpretations" },
          {
            type: "ul",
            items: [
              "Some intermediate-state courts apply a hybrid totality-of-circumstances analysis.",
              "Estoppel may apply if plaintiff continued to accept performance post-delay.",
            ],
          },
          { type: "h3", text: "VI. Conclusion" },
          {
            type: "p",
            text: "Brief the favorable circuit's standard early to anchor the analysis. Plead estoppel as backstop. Outcome depends materially on venue.",
          },
          { type: "h3", text: "VII. Additional Facts Needed" },
          {
            type: "ul",
            items: [
              "Final forum-selection clause and any prior venue stipulations.",
              "Communications evidencing plaintiff's continued acceptance.",
            ],
          },
        ],
      },
      {
        key: "perplexity",
        name: "Perplexity",
        accent: "#20808d",
        logoCdn: "perplexity",
        body: [
          { type: "h3", text: "I. Issue(s) Presented" },
          {
            type: "ul",
            items: [
              "Whether the law of cure tender favors enforcement where the cure was timely and commercially reasonable.",
            ],
          },
          { type: "h3", text: "II. Relevant Rules / Legal Principles" },
          {
            type: "ul",
            items: [
              "Cure-tender doctrine — established in Bartus v. Riccardi, 55 Misc.2d 3.",
              "Recent 2024 2nd Cir. trend: strict TOE enforcement where expressly stated.",
              "Recent 9th Cir. opinion: cure window controls absent prejudice.",
            ],
          },
          { type: "h3", text: "III. Analysis" },
          {
            type: "p",
            text: "Across 17 cases in 4 jurisdictions reviewed, recent decisions favor enforcement of cure provisions where tender was timely and commercially reasonable. The trend is consistent across all federal circuits surveyed.",
          },
          { type: "h3", text: "IV. Application" },
          {
            type: "ul",
            items: [
              "Strength: cure-doctrine caselaw is supportive and recent.",
              "Weakness: limited direct precedent at the trial-court level in this district.",
              "Risk: LOW-MEDIUM on the legal-authority question.",
            ],
          },
          { type: "h3", text: "V. Alternative Interpretations" },
          {
            type: "ul",
            items: [
              "Older authority in some jurisdictions disfavors cure tender absent express provision.",
              "Recent commercial-litigation trends move toward enforcement.",
            ],
          },
          { type: "h3", text: "VI. Conclusion" },
          {
            type: "p",
            text: "Legal authority strongly supports the defense. Cite Bartus v. Riccardi as the foundational case and the recent 9th Circuit opinion for modern application.",
          },
          { type: "h3", text: "VII. Additional Facts Needed" },
          {
            type: "ul",
            items: [
              "Updated Shepardization of Bartus v. Riccardi.",
              "District-specific precedent on cure tender.",
            ],
          },
        ],
      },
      {
        key: "grok",
        name: "Grok",
        accent: "#111827",
        logo: "/grok-icon.jpeg",
        body: [
          { type: "h3", text: "I. Issue(s) Presented" },
          {
            type: "ul",
            items: [
              "Whether the cure tender, on the existing record, defeats material breach as a matter of law.",
              "Whether opposing counsel can credibly resurrect TOE on cross-examination.",
            ],
          },
          { type: "h3", text: "II. Relevant Rules / Legal Principles" },
          {
            type: "ul",
            items: [
              "Adversarial framing: assume opposing counsel reads TOE strictly.",
              "Material breach is the lower-cost path for plaintiff — anticipate aggressive litigation.",
              "Mitigation doctrine cuts both ways; cure-refusal evidence must be airtight.",
            ],
          },
          { type: "h3", text: "III. Analysis" },
          {
            type: "p",
            text: "Red-team view: the defense theory is strong on the merits but exposed on rhetorical framing. Opposing counsel will narrate the delay as a calculated breach, not a curable defect. Counter-narrative needs to land first.",
          },
          { type: "h3", text: "IV. Application" },
          {
            type: "ul",
            items: [
              "Strength: cure tender is documented and timely on its face.",
              "Weakness: lack of buyer-acceptance evidence weakens waiver theory.",
              "Risk: MEDIUM — outcome depends as much on narrative as on doctrine.",
            ],
          },
          { type: "h3", text: "V. Alternative Interpretations" },
          {
            type: "ul",
            items: [
              "Opposing counsel may frame cure tender as bad-faith damage control.",
              "Juries weigh narrative heavily — preempt with a clean timeline.",
            ],
          },
          { type: "h3", text: "VI. Conclusion" },
          {
            type: "p",
            text: "Defense is defensible but rhetorically exposed. Lead with a clean timeline narrative; don't let opposing counsel define the breach as wilful.",
          },
          { type: "h3", text: "VII. Additional Facts Needed" },
          {
            type: "ul",
            items: [
              "Internal communications around the cure tender (intent evidence).",
              "Any prior course of dealing showing similar late deliveries accepted.",
            ],
          },
        ],
      },
    ],
    [],
  );

  // Model selector — pick up to 4 of 5
  const MAX_SELECTED = 4;
  const [selectedModelKeys, setSelectedModelKeys] = useState([
    "chatgpt",
    "claude",
    "gemini",
    "perplexity",
  ]);
  const toggleModelKey = (k) => {
    setSelectedModelKeys((prev) => {
      if (prev.includes(k)) {
        // don't allow going below 1 selection
        return prev.length > 1 ? prev.filter((x) => x !== k) : prev;
      }
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, k];
    });
  };
  const selectedModels = useMemo(
    () => heroModels.filter((m) => selectedModelKeys.includes(m.key)),
    [heroModels, selectedModelKeys],
  );

  // Research-page tab state (interactive but non-functional)
  const [activeResearchTab, setActiveResearchTab] = useState("comparison");
  const [hoverInfo, setHoverInfo] = useState(null); // 'comparison' | 'final' | null

  const heroPreviewRef = useRef(null);

  // ---------- Besthire-style scroll-driven 3D tilt on hero card ----------
  // Card starts laying back (rotateX 22deg, slightly scaled & translated down,
  // light blur), then "stands up" as the user scrolls it toward viewport center.
  const { scrollYProgress: heroCardProgress } = useScroll({
    target: heroPreviewRef,
    offset: ["start end", "center center"],
  });
  const springProgress = useSpring(heroCardProgress, {
    stiffness: 120,
    damping: 22,
    mass: 0.4,
  });
  const heroRotateX = useTransform(springProgress, [0, 1], [12, 0]);
  const heroScale = useTransform(springProgress, [0, 1], [0.97, 1]);
  const heroY = useTransform(springProgress, [0, 1], [24, 0]);
  const heroBlur = useTransform(
    springProgress,
    [0, 1],
    ["blur(1.5px)", "blur(0px)"],
  );
  const heroOpacity = useTransform(
    springProgress,
    [0, 0.6, 1],
    [0.85, 0.98, 1],
  );

  // ---------- Debate-page replica ----------
  const debateRoles = useMemo(
    () => [
      {
        key: "advocate",
        title: "Affirmative Advocate",
        subtitle: "Builds the strongest case for the attorney's position.",
        model: "ChatGPT",
        accent: "#10a37f",
        logo: "/openai-icon.svg",
      },
      {
        key: "critic",
        title: "Critical Analyst",
        subtitle: "Stress-tests arguments and exposes weaknesses.",
        model: "Claude",
        accent: "#d97757",
        logoCdn: "claude",
      },
      {
        key: "strategist",
        title: "Strategic / Procedural Analyst",
        subtitle: "Evaluates venue, timing, and procedural posture.",
        model: "Gemini",
        accent: "#4285f4",
        logoCdn: "googlegemini",
      },
    ],
    [],
  );

  const debateRounds = useMemo(
    () => [
      {
        round: 1,
        arguments: {
          advocate: {
            heading: "Opening — Substantial Performance",
            bullets: [
              "Buyer received the essential benefit; partial delivery was tendered and accepted.",
              "Cure window in §8.3 was expressly preserved — TOE strict reading is foreclosed.",
              "Restatement §237 controls: substantial performance defeats material breach.",
            ],
          },
          critic: {
            heading: "Initial Weaknesses",
            bullets: [
              "TOE clause was express, not implied — Advocate understates its weight.",
              "No record evidence of buyer's affirmative acceptance post-cure.",
              "Reliance on §237 ignores forum-specific strictness in the 2nd Circuit.",
            ],
          },
          strategist: {
            heading: "Procedural Posture",
            bullets: [
              "Venue is the central lever — lock favorable forum before motions practice.",
              "Burden on movant is heavy at this stage; framing matters more than authority.",
              "Sequence: motion to enforce forum-selection first, then substantive defense.",
            ],
          },
        },
      },
      {
        round: 2,
        arguments: {
          advocate: {
            heading: "Reinforce — Cure Tender Doctrine",
            bullets: [
              "Cure tender was timely, in writing, and commercially reasonable.",
              "Buyer's silence after tender supports waiver of strict timing.",
              "Even strict-TOE jurisdictions recognize cure where tender is acted upon.",
            ],
          },
          critic: {
            heading: "Sharpen — UCC Predominance Risk",
            bullets: [
              "Goods-or-services dominance is unsettled; loss of UCC §2-508 hurts fallback.",
              "Advocate has not addressed how to read §8.3 against industry custom.",
              "Damage exposure persists if mitigation log is incomplete.",
            ],
          },
          strategist: {
            heading: "Tactical Sequencing",
            bullets: [
              "File predominant-purpose declaration alongside answer to preserve UCC theory.",
              "Move to bifurcate damages early — keeps cure dispute clean on liability.",
              "Preserve appellate posture by anchoring on §237 in trial brief.",
            ],
          },
        },
      },
      {
        round: 3,
        arguments: {
          advocate: {
            heading: "Closing — Defense Theory Locked",
            bullets: [
              "Substantial performance + cure tender defeats material breach on the record.",
              "Cite Bartus v. Riccardi for modern application of the cure doctrine.",
              "Waiver via continued acceptance is the cleanest path to summary judgment.",
            ],
          },
          critic: {
            heading: "Closing — Residual Risks",
            bullets: [
              "If TOE survives, defense weakens by ~30%; backup theories required.",
              "Authority is favorable but trial-court precedent in district is thin.",
              "Document mitigation contemporaneously or risk damages reset on appeal.",
            ],
          },
          strategist: {
            heading: "Closing — Strategic Recommendations",
            bullets: [
              "Open with venue motion, anchor brief in §237, preserve UCC §2-508 as alt theory.",
              "Plead estoppel in the alternative for backstop on strict-TOE reading.",
              "Position is procedurally strong; substantive defense is supported but contestable.",
            ],
          },
        },
      },
    ],
    [],
  );

  const finalConsensus = useMemo(
    () => ({
      strength: "STRONG",
      bullets: [
        "Cross-role consensus: substantial performance + timely cure tender defeats the material-breach element on the existing record.",
        "Primary risk centers on venue and express TOE interpretation — both manageable with early forum-selection motion.",
        "Plead UCC §2-508 in the alternative; build mitigation documentation contemporaneously to defeat damages on the back end.",
        "Final synthesis: defensive theory is well-supported across IRAC, but requires venue lock and disciplined record-building to convert into summary judgment.",
      ],
    }),
    [],
  );

  // Expanded round state: object keyed by round number; 0 = final consensus
  const [expandedRounds, setExpandedRounds] = useState({ 1: true, 0: true });
  const [debatePlaying, setDebatePlaying] = useState(false);
  const [visibleRounds, setVisibleRounds] = useState(3);

  const [selectedRounds, setSelectedRounds] = useState(3);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleRound = (key) =>
    setExpandedRounds((s) => ({ ...s, [key]: !s[key] }));

  const runDebate = () => {
    if (selectedRounds > 3) {
      setShowUnlockModal(true);
      return;
    }
    setVisibleRounds(0);
    setExpandedRounds({ 1: true });
    setDebatePlaying(true);
  };

  useEffect(() => {
    if (!debatePlaying) return;
    if (visibleRounds >= debateRounds.length + 1) {
      // +1 represents final consensus row
      setDebatePlaying(false);
      return;
    }
    const id = setTimeout(() => {
      setVisibleRounds((r) => {
        const next = r + 1;
        setExpandedRounds((s) => ({ ...s, [next]: true }));
        return next;
      });
    }, 1100);
    return () => clearTimeout(id);
  }, [debatePlaying, visibleRounds, debateRounds.length]);

  // ---------- Interactive: Model picker ----------
  const models = [
    {
      key: "gpt",
      name: "GPT-4o",
      tag: "Reasoning",
      desc: "Broad legal reasoning, drafting, and synthesis across jurisdictions.",
      accent: "#10a37f",
    },
    {
      key: "claude",
      name: "Claude",
      tag: "Analysis",
      desc: "Long-context analysis of contracts, depositions, and case law.",
      accent: "#d97757",
    },
    {
      key: "gemini",
      name: "Gemini",
      tag: "Research",
      desc: "Multi-document research and structured fact extraction.",
      accent: "#4285f4",
    },
    {
      key: "perplexity",
      name: "Perplexity",
      tag: "Citations",
      desc: "Live, source-linked legal citations and recent precedent.",
      accent: "#20808d",
    },
    {
      key: "grok",
      name: "Grok",
      tag: "Adversarial",
      desc: "Aggressive counter-argumentation and red-team challenges.",
      accent: "#111827",
    },
  ];
  const [activeModel, setActiveModel] = useState("gpt");
  const activeModelObj = models.find((m) => m.key === activeModel);

  // ---------- Interactive: Workflow stepper ----------
  const workflow = [
    {
      icon: <FileSearch size={20} />,
      title: "Upload",
      body: "Drop in contracts, briefs, depositions, or case files. PDF, DOCX, and TXT supported.",
    },
    {
      icon: <Brain size={20} />,
      title: "Analyze",
      body: "Five AI models read the documents in parallel — extracting facts, claims, and risks.",
    },
    {
      icon: <MessagesSquare size={20} />,
      title: "Debate",
      body: "Models argue both sides of your issue, surfacing weaknesses you'd otherwise miss.",
    },
    {
      icon: <BookOpenCheck size={20} />,
      title: "Synthesize",
      body: "LawFusion produces a single, citation-backed answer with your strongest path forward.",
    },
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % workflow.length), 3000);
    return () => clearInterval(id);
  }, [workflow.length]);

  // ---------- Interactive: Stats counter ----------
  const stats = [
    { label: "Hours saved per matter", value: 28, suffix: "+" },
    { label: "Models reasoning together", value: 5, suffix: "" },
    { label: "Citation accuracy", value: 99, suffix: "%" },
    { label: "Lawyers onboarded", value: 12, suffix: "k+" },
  ];

  // ---------- FAQ ----------
  const faqs = [
    {
      q: "How is LawFusion different from ChatGPT?",
      a: "ChatGPT is one model. LawFusion orchestrates five — GPT-4o, Claude, Gemini, Perplexity, and Grok — so each result is cross-checked, debated, and synthesized into a single defensible answer.",
    },
    {
      q: "Is my client data confidential?",
      a: "Yes. Documents are encrypted in transit and at rest. We never train models on your data, and uploads can be permanently deleted at any time.",
    },
    {
      q: "What document formats are supported?",
      a: "PDFs, Word documents, plain text, and pasted content. Long-context models handle multi-hundred-page filings without chunking artifacts.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes. Plans are month-to-month with no commitment. You keep access through the end of your billing period.",
    },
    {
      q: "Do you offer a free trial?",
      a: "Yes — every new account starts with a free trial including debate mode, document analysis, and multi-model research.",
    },
  ];
  const [openFaq, setOpenFaq] = useState(0);

  // ---------- Testimonials ----------
  const testimonials = [
    {
      quote:
        "LawFusion's debate mode found a weakness in my motion I would have missed. It's like having a junior associate, a senior partner, and an opposing counsel in the room at the same time.",
      name: "Sarah Mitchell",
      title: "Solo Litigation Attorney",
    },
    {
      quote:
        "We replaced three separate AI subscriptions with LawFusion. The multi-model synthesis is something none of them can do alone.",
      name: "David Park",
      title: "Managing Partner, Park & Associates",
    },
    {
      quote:
        "Cut my research time by 70%. The citations are real, the reasoning is transparent, and it catches arguments I missed.",
      name: "Elena Rossi",
      title: "Appellate Practice, Boutique Firm",
    },
  ];
  const [tIndex, setTIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setTIndex((i) => (i + 1) % testimonials.length),
      5000,
    );
    return () => clearInterval(id);
  }, [testimonials.length]);

  // ---------- Pricing tiers (mirrors /pricing) ----------
  const tiers = [
    {
      name: "Essentials",
      price: "$19.95",
      period: "/month",
      actions: "50 AI Actions",
      blurb: "For solo attorneys getting started with multi-AI research.",
      features: [
        "Full access to the LawFusion Research, Intelligence & Debate Engine",
        "50 AI Actions per month",
        "Up to 25 saved projects securely stored",
        "Purchase additional AI Actions at $10 per 25 AI Actions",
      ],
      cta: "Get started",
      featured: false,
    },
    {
      name: "Professional",
      price: "$29.95",
      period: "/month",
      actions: "100 AI Actions",
      blurb: "Designed for attorneys who want more room for research, drafting, and argument testing.",
      features: [
        "Full access to the LawFusion Research, Intelligence & Debate Engine",
        "Designed for attorneys who want more room for research, drafting, and argument testing",
        "100 AI Actions per month",
        "Up to 50 saved projects securely stored",
        "Purchase additional AI Actions at $10 per 25 AI Actions",
      ],
      cta: "Get started",
      featured: true,
    },
  ];

  // ---------- Powered-by logos (matches main landing) ----------
  const poweredLogos = [
    { src: `/openai-icon.svg`, alt: "ChatGPT / OpenAI", caption: "ChatGPT", fallback: null },
    {
      src: `https://cdn.simpleicons.org/claude/${darkMode ? "ffffff" : "000000"}`,
      alt: "Claude",
      caption: "Claude",
      fallback: `https://cdn.simpleicons.org/anthropic/${darkMode ? "ffffff" : "000000"}`,
    },
    {
      src: `https://cdn.simpleicons.org/googlegemini/${darkMode ? "ffffff" : "000000"}`,
      alt: "Google Gemini",
      caption: "Gemini",
      fallback: null,
    },
    {
      src: `https://cdn.simpleicons.org/perplexity/${darkMode ? "ffffff" : "000000"}`,
      alt: "Perplexity AI",
      caption: "Perplexity",
      fallback: null,
    },
    { src: `/grok-icon.jpeg`, alt: "Grok (xAI)", caption: "Grok", fallback: null },
  ];

  // ---------- Use cases (interactive tabs) ----------
  const useCases = [
    {
      key: "litigation",
      title: "Litigation",
      desc: "Stress-test arguments before opposing counsel does. Run pro/con debates on every theory in your brief.",
      points: [
        "Pre-emptive weakness detection",
        "Counter-argument generation",
        "Precedent-aware reasoning",
      ],
    },
    {
      key: "contracts",
      title: "Contracts",
      desc: "Five models read each clause and flag risk, ambiguity, and non-standard terms — with negotiation suggestions.",
      points: [
        "Clause-by-clause review",
        "Risk scoring",
        "Redline suggestions",
      ],
    },
    {
      key: "research",
      title: "Research",
      desc: "Multi-jurisdiction case research with live citations. Synthesis layer reconciles disagreements between models.",
      points: [
        "Live citation lookup",
        "Cross-jurisdiction synthesis",
        "Source-linked answers",
      ],
    },
    {
      key: "discovery",
      title: "Discovery",
      desc: "Surface key facts, dates, and contradictions across thousands of pages — without missing the small stuff.",
      points: [
        "Long-context document review",
        "Timeline reconstruction",
        "Contradiction detection",
      ],
    },
  ];
  const [useCase, setUseCase] = useState("litigation");
  const activeUseCase = useCases.find((u) => u.key === useCase);

  // ---------- Refs / scroll anim ----------
  const debateRef = useRef(null);

  return (
    <div className="nl-page">
      <SEO
        title="LawFusion — Multi-AI Legal Intelligence"
        description="The legal AI platform that orchestrates five AI models to research, debate, and synthesize stronger arguments."
      />
      <Header />

      {/* ============ HERO ============ */}
      <section className="nl-hero">
        <div className="nl-hero-bg" aria-hidden="true">
          <div className="nl-orb nl-orb-1" />
          <div className="nl-orb nl-orb-2" />
          <div className="nl-grid-overlay" />
        </div>

        <div className="nl-container nl-hero-inner">
          <motion.div
            className="nl-eyebrow"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles size={14} />
            <span>Five AI models. One defensible answer.</span>
          </motion.div>

          <motion.h1
            className="nl-h1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            The fastest way to stronger
            <br />
            legal{" "}
            <span className="nl-rotate-wrap">
              <AnimatePresence mode="wait">
                <motion.span
                  key={rotatingWords[wordIndex]}
                  className="nl-rotate-word"
                  initial={{ rotateX: 90, opacity: 0, y: 15 }}
                  animate={{ rotateX: 0, opacity: 1, y: 0 }}
                  exit={{ rotateX: -90, opacity: 0, y: -15 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                  {rotatingWords[wordIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.h1>

          <motion.p
            className="nl-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            LawFusion orchestrates GPT-4o, Claude, Gemini, Perplexity, and Grok
            into a single multi-model legal workspace — built for attorneys who
            want depth, not just speed.
          </motion.p>

          <motion.div
            className="nl-cta-row"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <button
              className="nl-btn nl-btn-primary"
              onClick={() => navigate("/signup")}
            >
              Start free <ArrowRight size={16} />
            </button>
            <button
              className="nl-btn nl-btn-ghost"
              onClick={() =>
                debateRef.current?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Play size={14} /> See it in action
            </button>
          </motion.div>

          <motion.div
            className="nl-hero-meta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {/* <span className="nl-meta-item">
              <CheckCircle2 size={14} /> No credit card required
            </span> */}
            <span className="nl-meta-item">
              <CheckCircle2 size={14} /> SOC 2 ready
            </span>
            <span className="nl-meta-item">
              <CheckCircle2 size={14} /> Cancel anytime
            </span>
          </motion.div>

          {/* Hero — research page replica (3D scroll-tilt stage) */}
          <div className="nl-hero-stage" ref={heroPreviewRef}>
          <motion.div
            className="nl-research-app"
            style={{
              rotateX: heroRotateX,
              scale: heroScale,
              y: heroY,
              filter: heroBlur,
              opacity: heroOpacity,
              transformPerspective: 1400,
              transformOrigin: "50% 100%",
              willChange: "transform, filter",
            }}
          >
            <div className="nl-research-topbar">
              <div className="nl-research-tab-pill">
                <span className="nl-tp-dot" />
                Matter #2024-0451 · Breach of Contract
              </div>
              <div className="nl-research-topbar-right">
                <span className="nl-research-badge">
                  <CheckCircle2 size={12} /> All 4 models responded
                </span>
              </div>
            </div>

            <div className="nl-research-shell">
              {/* Query input row */}
              <div className="nl-research-query">
                <label className="nl-research-label">Research Query</label>
                <div className="nl-research-textarea">
                  {heroQuery}
                </div>
                <div className="nl-research-query-actions">
                  <button type="button" className="nl-research-attach">
                    <Paperclip size={14} /> Attach file
                  </button>
                  <button type="button" className="nl-research-run">
                    <Send size={14} /> Run Analysis
                  </button>
                </div>
              </div>

              {/* Model selector — pick up to 4 of 5 */}
              <div className="nl-model-picker">
                <div className="nl-model-picker-head">
                  <label className="nl-research-label">
                    Select AI models
                  </label>
                  <span className="nl-model-picker-count">
                    {selectedModelKeys.length}/{MAX_SELECTED} selected
                  </span>
                </div>
                <div className="nl-model-picker-grid">
                  {heroModels.map((m) => {
                    const isOn = selectedModelKeys.includes(m.key);
                    const isCapped =
                      !isOn && selectedModelKeys.length >= MAX_SELECTED;
                    return (
                      <button
                        type="button"
                        key={m.key}
                        className={`nl-model-chip ${isOn ? "is-on" : ""} ${
                          isCapped ? "is-capped" : ""
                        }`}
                        onClick={() => toggleModelKey(m.key)}
                        disabled={isCapped}
                        style={
                          isOn
                            ? {
                                borderColor: m.accent,
                                background: `${m.accent}14`,
                              }
                            : undefined
                        }
                      >
                        <span
                          className="nl-model-chip-check"
                          style={{
                            background: isOn ? m.accent : "transparent",
                            borderColor: isOn ? m.accent : "var(--nl-border-strong)",
                          }}
                        >
                          {isOn && (
                            <CheckCircle2 size={11} strokeWidth={3} color="#fff" />
                          )}
                        </span>
                        <span
                          className="nl-model-chip-logo"
                          style={{
                            background: `${m.accent}1a`,
                            borderColor: `${m.accent}40`,
                          }}
                        >
                          <img
                            src={
                              m.logo
                                ? m.logo
                                : `https://cdn.simpleicons.org/${m.logoCdn}/${m.accent.replace("#", "")}`
                            }
                            alt={m.name}
                          />
                        </span>
                        <span
                          className="nl-model-chip-name"
                          style={{ color: isOn ? m.accent : undefined }}
                        >
                          {m.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Response cards — only selected models */}
              <div className="nl-research-responses">
                {selectedModels.map((m, mi) => (
                  <motion.div
                    className="nl-response-card"
                    key={m.name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{
                      delay: mi * 0.08,
                      type: "spring",
                      stiffness: 280,
                      damping: 22,
                    }}
                    whileHover={{ y: -4 }}
                  >
                    <div
                      className="nl-response-accent"
                      style={{
                        background: `linear-gradient(90deg, ${m.accent}, ${m.accent}88)`,
                      }}
                    />
                    <div className="nl-response-inner">
                      <div className="nl-response-head">
                        <span
                          className="nl-response-logo-wrap"
                          style={{
                            background: `${m.accent}1a`,
                            borderColor: `${m.accent}40`,
                          }}
                        >
                          {m.logo ? (
                            <img
                              src={m.logo}
                              alt={m.name}
                              className="nl-response-logo"
                            />
                          ) : (
                            <img
                              src={`https://cdn.simpleicons.org/${m.logoCdn}/${m.accent.replace("#", "")}`}
                              alt={m.name}
                              className="nl-response-logo"
                            />
                          )}
                        </span>
                        <h4
                          className="nl-response-title"
                          style={{ color: m.accent }}
                        >
                          {m.name}'s Response Analysis
                        </h4>
                      </div>

                      <div className="nl-response-html">
                        {m.body.map((b, bi) => {
                          if (b.type === "h3")
                            return <h3 key={bi}>{b.text}</h3>;
                          if (b.type === "p")
                            return <p key={bi}>{b.text}</p>;
                          if (b.type === "ul")
                            return (
                              <ul key={bi}>
                                {b.items.map((it, ii) => (
                                  <li key={ii}>{it}</li>
                                ))}
                              </ul>
                            );
                          return null;
                        })}
                      </div>

                      <div className="nl-response-actions">
                        <button type="button" className="nl-response-btn">
                          Edit / Export
                        </button>
                        <div className="nl-response-actions-right">
                          <span className="nl-response-status">
                            <CheckCircle2 size={14} /> Complete
                          </span>
                          <button
                            type="button"
                            className="nl-response-delete"
                            aria-label="Remove"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Tabs: Comparative Analysis | Final Synthesis */}
              <div className="nl-tabs-list" role="tablist">
                <motion.button
                  type="button"
                  role="tab"
                  className={`nl-tab-trigger ${
                    activeResearchTab === "comparison" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveResearchTab("comparison")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Comparative Analysis</span>
                  <span
                    className="nl-tab-info"
                    onMouseEnter={() => setHoverInfo("comparison")}
                    onMouseLeave={() => setHoverInfo(null)}
                  >
                    <Info size={13} />
                  </span>
                  <AnimatePresence>
                    {hoverInfo === "comparison" && (
                      <motion.div
                        className="nl-tab-tooltip"
                        initial={{ opacity: 0, y: 5, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                      >
                        Summarize and compare key points from multiple AI
                        responses.
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>

                <motion.button
                  type="button"
                  role="tab"
                  className={`nl-tab-trigger ${
                    activeResearchTab === "final" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveResearchTab("final")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Final Synthesis</span>
                  <span
                    className="nl-tab-info"
                    onMouseEnter={() => setHoverInfo("final")}
                    onMouseLeave={() => setHoverInfo(null)}
                  >
                    <Info size={13} />
                  </span>
                  <AnimatePresence>
                    {hoverInfo === "final" && (
                      <motion.div
                        className="nl-tab-tooltip"
                        initial={{ opacity: 0, y: 5, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                      >
                        Create one cohesive summary based on the best AI
                        responses.
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>

              {/* Tab content panel */}
              <AnimatePresence mode="wait">
                {activeResearchTab === "comparison" ? (
                  <motion.div
                    key="comparison"
                    className="nl-tab-panel"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="nl-panel-head">
                      <h3>Comparative Analysis</h3>
                      <div className="nl-panel-meta">
                        <span>
                          <Sparkles size={12} /> 4 models compared
                        </span>
                        <button className="nl-panel-action" type="button">
                          <Copy size={12} /> Copy
                        </button>
                      </div>
                    </div>
                    <div className="nl-panel-body nl-response-html">
                      <h3>1. Areas of Agreement</h3>
                      <ul>
                        <li>
                          All four models converge on <strong>substantial
                          performance</strong> as the strongest defensive
                          theory.
                        </li>
                        <li>
                          Unanimous view that the cure tender, properly
                          documented, defeats the material-breach element.
                        </li>
                      </ul>

                      <h3>2. Areas of Disagreement</h3>
                      <ul>
                        <li>
                          ChatGPT &amp; Claude emphasize the contractual cure
                          window; Gemini centers jurisdictional split;
                          Perplexity anchors on caselaw trend.
                        </li>
                        <li>
                          Models split on whether UCC §2-508 (Claude) or
                          common-law cure (ChatGPT) controls.
                        </li>
                      </ul>

                      <h3>3. Reasoning Quality Differences</h3>
                      <ul>
                        <li>
                          ChatGPT &amp; Claude rely on fact-specific framing;
                          Gemini relies on abstract jurisdictional doctrine.
                        </li>
                        <li>
                          Perplexity's analysis is citation-heavy but
                          authority-light at the trial-court level.
                        </li>
                      </ul>

                      <h3>4. Missing Facts Identified</h3>
                      <ul>
                        <li>Final venue / forum-selection clause.</li>
                        <li>Predominant-purpose evidence (goods vs. services).</li>
                        <li>Plaintiff's post-cure mitigation log.</li>
                      </ul>

                      <h3>5. Critical Vulnerabilities (Ranked)</h3>
                      <ul>
                        <li>
                          <strong>1. TOE Clause Strictness — CRITICAL</strong>
                          <br />
                          RISK: Express TOE may override substantial
                          performance.
                          <br />
                          CONSEQUENCE: Defense theory collapses in strict
                          jurisdictions.
                          <br />
                          ACTION: Confirm venue; brief favorable standard.
                        </li>
                        <li>
                          <strong>2. UCC Applicability</strong>
                          <br />
                          RISK: Service-dominant reading narrows cure rights.
                          <br />
                          CONSEQUENCE: Loss of statutory cure fallback.
                          <br />
                          ACTION: Develop predominant-purpose evidence.
                        </li>
                      </ul>

                      <h3>6. Additional Facts Needed</h3>
                      <ul>
                        <li>
                          <strong>Cure Evidence:</strong> Full email chain
                          post-tender; documented refusal grounds.
                        </li>
                        <li>
                          <strong>Authority:</strong> District-specific cure-
                          tender precedent.
                        </li>
                      </ul>

                      <h3>7. Material Disagreements with Recommended Position</h3>
                      <p>
                        Claude recommends leading with UCC §2-508; ChatGPT
                        recommends common-law substantial performance.{" "}
                        <strong>RECOMMENDATION:</strong> lead with common-law
                        theory, plead UCC §2-508 in the alternative.
                      </p>

                      <h3>8. Strategic Assessment</h3>
                      <p>
                        Overall position:{" "}
                        <strong className="nl-strong-strong">STRONG</strong>.
                        Cross-model consensus on the defensive theory plus
                        favorable recent caselaw outweigh the TOE risk if venue
                        is properly managed.
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="final"
                    className="nl-tab-panel"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="nl-panel-head">
                      <h3>Final Synthesis</h3>
                      <div className="nl-panel-meta">
                        <span className="nl-panel-strength">
                          Position:{" "}
                          <strong className="nl-strong-strong">STRONG</strong>
                        </span>
                        <button className="nl-panel-action" type="button">
                          <Copy size={12} /> Copy
                        </button>
                      </div>
                    </div>
                    <div className="nl-panel-body nl-response-html">
                      <h3>1. Issue(s) Presented</h3>
                      <ul>
                        <li>
                          Whether late delivery constitutes material breach
                          where partial cure was tendered within the
                          contractual cure window.
                        </li>
                      </ul>

                      <h3>2. Relevant Rules / Legal Principles</h3>
                      <ul>
                        <li>
                          Restatement (Second) of Contracts §237 —
                          substantial-performance doctrine.
                        </li>
                        <li>
                          UCC §2-508 — statutory right of cure for goods.
                        </li>
                        <li>
                          Cure-tender doctrine, recognized in{" "}
                          <em>Bartus v. Riccardi</em>, 55 Misc.2d 3.
                        </li>
                      </ul>

                      <h3>3. Application / Analysis</h3>
                      <p>
                        All four models concur that timely cure tender
                        preserves the bargain's essential benefit. ChatGPT and
                        Claude anchor the defense in contractual and statutory
                        cure rights; Gemini frames the dispute jurisdictionally;
                        Perplexity supplies the supporting caselaw trend.
                      </p>

                      <h3>4. Vulnerability Analysis</h3>
                      <ul>
                        <li>
                          <strong>PRIMARY VULNERABILITY (CRITICAL):</strong>
                          <br />
                          Risk: Express TOE clause may override substantial
                          performance in strict jurisdictions.
                          <br />
                          Consequence: Defensive theory collapses; exposure
                          rises sharply.
                          <br />
                          Action needed: Lock venue early; brief the favorable
                          circuit's standard in the opening motion.
                        </li>
                        <li>
                          <strong>SECONDARY VULNERABILITIES:</strong>
                          <br />
                          1. UCC applicability — service-dominant reading
                          narrows cure rights.
                          <br />
                          2. Mitigation gap — plaintiff's post-cure conduct may
                          revive damages exposure.
                        </li>
                      </ul>

                      <h3>5. Strategic Posture Assessment</h3>
                      <p>
                        Position:{" "}
                        <strong className="nl-strong-strong">STRONG</strong>.
                        Cross-model consensus supports the defensive theory.
                        Material risk concentrated in venue and TOE
                        interpretation — both manageable.
                      </p>

                      <h3>6. Corrective Actions</h3>
                      <ul>
                        <li>
                          1. File forum-selection motion to lock favorable
                          venue.
                        </li>
                        <li>
                          2. Brief substantial-performance theory with cure-
                          tender documentation.
                        </li>
                        <li>
                          3. Develop predominant-purpose evidence for UCC §2-
                          508 fallback.
                        </li>
                        <li>
                          4. Build mitigation log to defeat damages on the
                          back end.
                        </li>
                      </ul>

                      <h3>7. Conclusion</h3>
                      <p>
                        Lead with substantial performance, anchor venue in the
                        favorable circuit, preserve UCC §2-508 as fallback, and
                        document mitigation contemporaneously. Position
                        materially strengthens upon execution of the corrective
                        actions above.
                      </p>

                      <h3>8. Additional Facts Needed</h3>
                      <ul>
                        <li>Final forum-selection clause and venue posture.</li>
                        <li>Bill of materials for predominant-purpose test.</li>
                        <li>Plaintiff's post-cure mitigation log.</li>
                        <li>District-specific cure-tender precedent.</li>
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* ============ LOGOS / TRUST STRIP ============ */}
      <section className="nl-trust">
        <div className="nl-container">
          <p className="nl-trust-label">Powered by the best in AI</p>
          <div className="nl-trust-row">
            {poweredLogos.map((logo) => (
              <motion.div
                className="nl-logo-item"
                key={logo.caption}
                whileHover={{ y: -4, scale: 1.06 }}
                transition={{ duration: 0.2 }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="nl-logo-img"
                  onError={(e) => {
                    if (logo.fallback) e.currentTarget.src = logo.fallback;
                  }}
                />
                <span className="nl-logo-caption">{logo.caption}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="nl-stats">
        <div className="nl-container nl-stats-grid">
          {stats.map((s) => (
            <div className="nl-stat-card" key={s.label}>
              <div className="nl-stat-value">
                {s.value}
                <span className="nl-stat-suffix">{s.suffix}</span>
              </div>
              <div className="nl-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ INTERACTIVE: DEBATE MODE REPLICA ============ */}
      <section className="nl-section nl-debate" ref={debateRef}>
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">Live demo · Debate Mode</span>
            <h2 className="nl-h2">
              Watch three AI roles argue your case in real time.
            </h2>
            <p className="nl-h2-sub">
              Debate Mode assigns each AI a role — Advocate, Critic, Strategist —
              and runs multi-round IRAC-style debate, ending in a Claude-led
              final consensus.
            </p>
          </div>

          <div className="nl-debate-app">
            {/* Top bar — query + rounds + run */}
            <div className="nl-debate-topbar">
              <div className="nl-debate-tab-pill">
                <span className="nl-tp-dot" />
                Debate · Matter #2024-0451
              </div>
              <div className="nl-debate-topbar-right">
                <span className="nl-debate-rounds-meta">
                  {debatePlaying ? (
                    <>
                      <Loader2 size={12} className="nl-spin" />
                      {visibleRounds <= debateRounds.length
                        ? `Round ${visibleRounds} of ${selectedRounds}…`
                        : "Final summary…"}
                    </>
                  ) : (
                    <div className="nl-debate-dropdown-container" ref={dropdownRef}>
                      <button
                        type="button"
                        className="nl-debate-dropdown-trigger"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                      >
                        <Sparkles size={12} />
                        <span>{selectedRounds}-round debate</span>
                        <ChevronDown size={12} className={`nl-dropdown-arrow ${dropdownOpen ? "is-open" : ""}`} />
                      </button>
                      {dropdownOpen && (
                        <div className="nl-debate-dropdown-menu">
                          <button
                            type="button"
                            className={`nl-debate-dropdown-item${selectedRounds === 3 ? " is-active" : ""}`}
                            onClick={() => {
                              setSelectedRounds(3);
                              setDropdownOpen(false);
                            }}
                          >
                            3-round debate <span className="nl-dropdown-badge-free">Free</span>
                          </button>
                          <button
                            type="button"
                            className={`nl-debate-dropdown-item${selectedRounds === 5 ? " is-active" : ""}`}
                            onClick={() => {
                              setSelectedRounds(5);
                              setDropdownOpen(false);
                            }}
                          >
                            5-round debate <span className="nl-dropdown-badge-premium">Trial</span>
                          </button>
                          <button
                            type="button"
                            className={`nl-debate-dropdown-item${selectedRounds === 8 ? " is-active" : ""}`}
                            onClick={() => {
                              setSelectedRounds(8);
                              setDropdownOpen(false);
                            }}
                          >
                            8-round debate <span className="nl-dropdown-badge-pro">Pro</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </span>
                <button
                  className="nl-debate-run-btn"
                  type="button"
                  onClick={runDebate}
                  disabled={debatePlaying}
                >
                  {debatePlaying ? "Running…" : "Run debate"}
                  <Play size={13} />
                </button>
              </div>
            </div>

            {/* Query banner */}
            <div className="nl-debate-query">
              <label className="nl-research-label">Debate Question</label>
              <div className="nl-research-textarea">
                Was the defendant's late delivery a material breach of contract,
                given that a partial cure was tendered within the contractual
                cure window?
              </div>
            </div>

            {/* Role assignment row */}
            <div className="nl-debate-roles">
              {debateRoles.map((r) => (
                <div className="nl-debate-role-card" key={r.key}>
                  <div
                    className="nl-debate-role-accent"
                    style={{ background: r.accent }}
                  />
                  <div className="nl-debate-role-head">
                    <span
                      className="nl-debate-role-logo"
                      style={{
                        background: `${r.accent}1a`,
                        borderColor: `${r.accent}40`,
                      }}
                    >
                      <img
                        src={
                          r.logo
                            ? r.logo
                            : `https://cdn.simpleicons.org/${r.logoCdn}/${r.accent.replace("#", "")}`
                        }
                        alt={r.model}
                      />
                    </span>
                    <div className="nl-debate-role-meta">
                      <span
                        className="nl-debate-role-title"
                        style={{ color: r.accent }}
                      >
                        {r.title}
                      </span>
                      <span className="nl-debate-role-model">
                        Assigned: <strong>{r.model}</strong>
                      </span>
                    </div>
                  </div>
                  <p className="nl-debate-role-sub">{r.subtitle}</p>
                </div>
              ))}
            </div>

            {/* Rounds + Final Consensus accordion */}
            <div className="nl-debate-rounds">
              {debateRounds.map((rd) => {
                const isOpen = !!expandedRounds[rd.round];
                const isVisible = !debatePlaying || rd.round <= visibleRounds;
                return (
                  <motion.div
                    className={`nl-debate-round ${isOpen ? "is-open" : ""}`}
                    key={rd.round}
                    initial={false}
                    animate={{
                      opacity: isVisible ? 1 : 0.35,
                      y: isVisible ? 0 : 8,
                    }}
                    transition={{ duration: 0.35 }}
                  >
                    <button
                      className="nl-debate-round-head"
                      onClick={() => toggleRound(rd.round)}
                      type="button"
                    >
                      <span className="nl-debate-round-num">
                        <span className="nl-debate-round-pill">
                          Round {rd.round}/3
                        </span>
                        {rd.round === 1 && "Opening Arguments"}
                        {rd.round === 2 && "Cross-Examination & Rebuttal"}
                        {rd.round === 3 && "Closing & Synthesis"}
                      </span>
                      <ChevronDown
                        size={18}
                        className={`nl-debate-chev ${isOpen ? "is-open" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          className="nl-debate-round-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="nl-debate-round-grid">
                            {debateRoles.map((role) => {
                              const arg = rd.arguments[role.key];
                              return (
                                <div
                                  className="nl-debate-arg-card"
                                  key={role.key}
                                >
                                  <div
                                    className="nl-debate-arg-strip"
                                    style={{ background: role.accent }}
                                  />
                                  <div className="nl-debate-arg-inner">
                                    <div className="nl-debate-arg-meta">
                                      <span
                                        className="nl-debate-arg-model"
                                        style={{ color: role.accent }}
                                      >
                                        {role.model}
                                      </span>
                                      <span className="nl-debate-arg-role">
                                        {role.title}
                                      </span>
                                    </div>
                                    <h5 className="nl-debate-arg-heading">
                                      {arg.heading}
                                    </h5>
                                    <ul className="nl-debate-arg-bullets">
                                      {arg.bullets.map((b, i) => (
                                        <li key={i}>{b}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}

              {/* Final Consensus & Summary */}
              <motion.div
                className={`nl-debate-round nl-debate-round-final ${
                  expandedRounds[0] ? "is-open" : ""
                }`}
                initial={false}
                animate={{
                  opacity:
                    !debatePlaying || visibleRounds > debateRounds.length
                      ? 1
                      : 0.35,
                  y:
                    !debatePlaying || visibleRounds > debateRounds.length
                      ? 0
                      : 8,
                }}
                transition={{ duration: 0.35 }}
              >
                <button
                  className="nl-debate-round-head"
                  onClick={() => toggleRound(0)}
                  type="button"
                >
                  <span className="nl-debate-round-num">
                    <span className="nl-debate-round-pill nl-debate-round-pill-final">
                      <Sparkles size={11} /> Final
                    </span>
                    Final Consensus &amp; Summary
                  </span>
                  <ChevronDown
                    size={18}
                    className={`nl-debate-chev ${
                      expandedRounds[0] ? "is-open" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {expandedRounds[0] && (
                    <motion.div
                      className="nl-debate-round-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="nl-debate-consensus-card">
                        <div className="nl-debate-consensus-head">
                          <div className="nl-debate-consensus-author">
                            <span
                              className="nl-debate-role-logo"
                              style={{
                                background: "#d977571a",
                                borderColor: "#d9775740",
                              }}
                            >
                              <img
                                src={`https://cdn.simpleicons.org/claude/d97757`}
                                alt="Claude"
                              />
                            </span>
                            <div>
                              <strong style={{ color: "#d97757" }}>
                                Claude
                              </strong>
                              <span className="nl-debate-arg-role">
                                Final Consensus Summary
                              </span>
                            </div>
                          </div>
                          <span className="nl-debate-strength">
                            Position:{" "}
                            <strong className="nl-strong-strong">
                              {finalConsensus.strength}
                            </strong>
                          </span>
                        </div>
                        <ul className="nl-debate-consensus-bullets">
                          {finalConsensus.bullets.map((b, i) => (
                            <li key={i}>
                              <CheckCircle2 size={13} />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="nl-section">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">What's inside</span>
            <h2 className="nl-h2">Everything a modern legal team needs.</h2>
            <p className="nl-h2-sub">
              Built ground-up for attorneys — not retrofitted from a generic
              chatbot.
            </p>
          </div>

          <div className="nl-features-grid">
            {[
              {
                icon: <Brain size={22} />,
                title: "Multi-AI Collaboration",
                body: "Five models read every prompt together — each challenging the others, surfacing depth a single AI can't reach.",
              },
              {
                icon: <MessagesSquare size={22} />,
                title: "Debate Mode",
                body: "Pit models on opposing sides of your legal issue and watch weaknesses appear before opposing counsel finds them.",
              },
              {
                icon: <FileSearch size={22} />,
                title: "Document Intelligence",
                body: "RAG-powered analysis of contracts, briefs, depositions — long-context aware, citation-backed.",
              },
              {
                icon: <Shield size={22} />,
                title: "Confidential by default",
                body: "Encrypted at rest and in transit. We never train models on your data. Delete uploads anytime.",
              },
              {
                icon: <Zap size={22} />,
                title: "Drafts in seconds",
                body: "Motions, briefs, demand letters, contract redlines — generated, then refined by the multi-model loop.",
              },
              {
                icon: <Layers size={22} />,
                title: "Unified workspace",
                body: "Matters, chats, files, and citations live in one place — searchable across every model you've used.",
              },
            ].map((f) => (
              <div className="nl-feature-card" key={f.title}>
                <div className="nl-feature-icon">{f.icon}</div>
                <h3 className="nl-feature-title">{f.title}</h3>
                <p className="nl-feature-body">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE: MODEL PICKER ============ */}
      <section className="nl-section nl-models-section">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">The panel</span>
            <h2 className="nl-h2">Meet the five models behind every answer.</h2>
            <p className="nl-h2-sub">
              Hover or tap to see how each model contributes its strengths to
              the synthesis.
            </p>
          </div>

          <div className="nl-models-layout">
            <div className="nl-models-list">
              {models.map((m) => (
                <button
                  key={m.key}
                  className={`nl-model-row ${
                    activeModel === m.key ? "is-active" : ""
                  }`}
                  onMouseEnter={() => setActiveModel(m.key)}
                  onClick={() => setActiveModel(m.key)}
                >
                  <span
                    className="nl-model-dot"
                    style={{ background: m.accent }}
                  />
                  <span className="nl-model-name">{m.name}</span>
                  <span className="nl-model-tag">{m.tag}</span>
                </button>
              ))}
            </div>

            <div className="nl-model-detail">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeModelObj.key}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div
                    className="nl-model-detail-badge"
                    style={{
                      background: `${activeModelObj.accent}1a`,
                      color: activeModelObj.accent,
                    }}
                  >
                    {activeModelObj.tag}
                  </div>
                  <h3 className="nl-model-detail-name">
                    {activeModelObj.name}
                  </h3>
                  <p className="nl-model-detail-body">{activeModelObj.desc}</p>
                  <div className="nl-model-bars">
                    <div className="nl-bar">
                      <span>Reasoning</span>
                      <div className="nl-bar-track">
                        <div
                          className="nl-bar-fill"
                          style={{
                            width: "92%",
                            background: activeModelObj.accent,
                          }}
                        />
                      </div>
                    </div>
                    <div className="nl-bar">
                      <span>Citations</span>
                      <div className="nl-bar-track">
                        <div
                          className="nl-bar-fill"
                          style={{
                            width: "78%",
                            background: activeModelObj.accent,
                          }}
                        />
                      </div>
                    </div>
                    <div className="nl-bar">
                      <span>Speed</span>
                      <div className="nl-bar-track">
                        <div
                          className="nl-bar-fill"
                          style={{
                            width: "85%",
                            background: activeModelObj.accent,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE: WORKFLOW STEPPER ============ */}
      <section className="nl-section nl-workflow">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">How it works</span>
            <h2 className="nl-h2">From question to answer in four steps.</h2>
          </div>

          <div className="nl-stepper">
            {workflow.map((w, i) => (
              <button
                key={w.title}
                className={`nl-step ${step === i ? "is-active" : ""} ${
                  step > i ? "is-done" : ""
                }`}
                onClick={() => setStep(i)}
              >
                <div className="nl-step-icon">{w.icon}</div>
                <div className="nl-step-meta">
                  <span className="nl-step-num">0{i + 1}</span>
                  <h4>{w.title}</h4>
                </div>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              className="nl-step-body"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
            >
              <h3>{workflow[step].title}</h3>
              <p>{workflow[step].body}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ============ INTERACTIVE: USE CASE TABS ============ */}
      <section className="nl-section nl-usecases">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">Use cases</span>
            <h2 className="nl-h2">Where attorneys win with LawFusion.</h2>
          </div>

          <div className="nl-tabs">
            {useCases.map((u) => (
              <button
                key={u.key}
                className={`nl-tab ${useCase === u.key ? "is-active" : ""}`}
                onClick={() => setUseCase(u.key)}
              >
                {u.title}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeUseCase.key}
              className="nl-usecase-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <div className="nl-usecase-left">
                <h3>{activeUseCase.title}</h3>
                <p>{activeUseCase.desc}</p>
                <ul>
                  {activeUseCase.points.map((p) => (
                    <li key={p}>
                      <CheckCircle2 size={16} /> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="nl-usecase-right">
                <div className="nl-usecase-illu">
                  <div className="nl-illu-row">
                    <Target size={18} />
                    <div className="nl-illu-bar" style={{ width: "78%" }} />
                  </div>
                  <div className="nl-illu-row">
                    <Clock size={18} />
                    <div className="nl-illu-bar" style={{ width: "62%" }} />
                  </div>
                  <div className="nl-illu-row">
                    <TrendingUp size={18} />
                    <div className="nl-illu-bar" style={{ width: "91%" }} />
                  </div>
                  <div className="nl-illu-row">
                    <Lock size={18} />
                    <div className="nl-illu-bar" style={{ width: "100%" }} />
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="nl-section nl-testimonials">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">Loved by attorneys</span>
            <h2 className="nl-h2">From solo practitioners to multi-state firms.</h2>
          </div>

          <div className="nl-testimonial-card">
            <Quote size={28} className="nl-quote-icon" />
            <AnimatePresence mode="wait">
              <motion.div
                key={tIndex}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
              >
                <p className="nl-quote-text">{testimonials[tIndex].quote}</p>
                <div className="nl-quote-meta">
                  <strong>{testimonials[tIndex].name}</strong>
                  <span>{testimonials[tIndex].title}</span>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="nl-quote-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`nl-dot ${tIndex === i ? "is-active" : ""}`}
                  onClick={() => setTIndex(i)}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="nl-section nl-pricing">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">Pricing</span>
            <h2 className="nl-h2">Simple plans. No hidden fees.</h2>
            <p className="nl-h2-sub">
              Start free. Upgrade when you need debate mode every day.
            </p>
          </div>

          <div className="nl-pricing-grid">
            {tiers.map((t) => (
              <div
                key={t.name}
                className={`nl-tier ${t.featured ? "is-featured" : ""}`}
              >
                {t.featured && <div className="nl-tier-flag">Most popular</div>}
                <h3 className="nl-tier-name">{t.name}</h3>
                <p className="nl-tier-blurb">{t.blurb}</p>
                <div className="nl-tier-price">
                  <span className="nl-tier-amt">{t.price}</span>
                  <span className="nl-tier-per">{t.period}</span>
                </div>
                {t.actions && (
                  <div className="nl-tier-actions">
                    <Zap size={14} /> {t.actions}
                  </div>
                )}
                <ul className="nl-tier-features">
                  {t.features.map((f) => (
                    <li key={f}>
                      <CheckCircle2 size={14} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  className={`nl-btn ${
                    t.featured ? "nl-btn-primary" : "nl-btn-ghost"
                  }`}
                  onClick={() => navigate("/pricing")}
                >
                  {t.cta} <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="nl-section nl-faq">
        <div className="nl-container">
          <div className="nl-section-head">
            <span className="nl-tag">FAQ</span>
            <h2 className="nl-h2">Answers to common questions.</h2>
          </div>

          <div className="nl-faq-list">
            {faqs.map((f, i) => (
              <div
                key={f.q}
                className={`nl-faq-item ${openFaq === i ? "is-open" : ""}`}
              >
                <button
                  className="nl-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <span>{f.q}</span>
                  <ChevronDown
                    size={18}
                    className={`nl-faq-chev ${openFaq === i ? "is-open" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      className="nl-faq-a"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <p>{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="nl-cta-final">
        <div className="nl-container">
          <div className="nl-cta-final-card">
            <h2>Start building stronger arguments today.</h2>
            <p>
              Join thousands of attorneys using LawFusion to research, draft,
              and debate — with five AI models on their side.
            </p>
            <div className="nl-cta-row nl-cta-center">
              <button
                className="nl-btn nl-btn-primary"
                onClick={() => navigate("/signup")}
              >
                Start free <ArrowRight size={16} />
              </button>
              <button
                className="nl-btn nl-btn-ghost-light"
                onClick={() => navigate("/pricing")}
              >
                See pricing
              </button>
            </div>
          </div>
        </div>
      </section>

      {showUnlockModal && (
        <div className="nl-modal-overlay">
          <div className="nl-modal-card">
            <button
              type="button"
              className="nl-modal-close"
              onClick={() => setShowUnlockModal(false)}
            >
              &times;
            </button>
            <div className="nl-modal-icon">
              <Sparkles size={28} />
            </div>
            <h3>Unlock {selectedRounds}-Round Debates</h3>
            <p>
              Deep adversarial debates with {selectedRounds} full rounds of AI analysis,
              cross-examination, and comprehensive strategic planning are available
              in our trial and premium plans.
            </p>
            <div className="nl-modal-features">
              <div className="nl-modal-feature-item">
                <CheckCircle2 size={16} /> Multi-model adversarial debate loop
              </div>
              <div className="nl-modal-feature-item">
                <CheckCircle2 size={16} /> Expose hidden litigation risks & vulnerabilities
              </div>
              <div className="nl-modal-feature-item">
                <CheckCircle2 size={16} /> Export citation-backed case strategies
              </div>
            </div>
            <div className="nl-modal-actions">
              <button
                type="button"
                className="nl-modal-btn-primary"
                onClick={() => navigate("/pricing")}
              >
                Start Free Trial
              </button>
              <button
                type="button"
                className="nl-modal-btn-secondary"
                onClick={() => {
                  setSelectedRounds(3);
                  setShowUnlockModal(false);
                }}
              >
                Continue with 3-round Preview
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer disableLazy />
    </div>
  );
};

export default NewLanding;
